import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  Tabs,
  Tab,
  Menu,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import ViewListIcon from "@mui/icons-material/ViewList";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Pencil, Package, Tags, DollarSign } from "lucide-react";
import API, { paginationNextPath } from "../utils/api";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";
import useAuth from "../hooks/useAuth";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";

const ADT_MONTHLY_CHARGE_ORDER = [
  "smart-alarm-com",
  "secure-alarm-com",
  "complete-alarm-com",
  "smart-with-touchscreen",
  "secure-with-touchscreen",
  "complete-with-touchscreen",
  "smart-without-touchscreen",
  "secure-without-touchscreen",
  "complete-without-touchscreen",
];

const WITH_TOUCHSCREEN_PACKAGE_KEYS = new Set([
  "smart-with-touchscreen",
  "secure-with-touchscreen",
  "complete-with-touchscreen",
]);

function catalogItemIsTouchscreen(product) {
  const haystack = `${product?.name || ""} ${product?.sku || ""}`.toLowerCase();
  return haystack.includes("touchscreen");
}

function compareMonthlyCharges(a, b) {
  const rank = (product) => {
    const index = ADT_MONTHLY_CHARGE_ORDER.indexOf(product.adt_package_key);
    return index === -1 ? ADT_MONTHLY_CHARGE_ORDER.length : index;
  };
  const diff = rank(a) - rank(b);
  if (diff !== 0) return diff;
  return String(a.name).localeCompare(String(b.name));
}

/** First letter of each word, including hyphenated parts. Rest of the word stays as typed. */
function capitalizeWords(value) {
  return String(value ?? "")
    .trim()
    .replace(/[^\s]+/g, (word) =>
      word
        .split("-")
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
        .join("-")
    );
}

export default function ProductsServicesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isReadOnly } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuProduct, setMenuProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filterType, setFilterType] = useState("all"); // all, product, service, monthly_charge, discount, packages
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [categoryError, setCategoryError] = useState("");
  const [inlineAdding, setInlineAdding] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [inlineForm, setInlineForm] = useState({
    name: "",
    category: "",
    price: "",
    product_type: "product",
  });
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    sku: "",
    product_type: "product",
    category: "",
  });

  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageSaving, setPackageSaving] = useState(false);
  const [packageForm, setPackageForm] = useState({
    name: "",
    description: "",
    sku: "",
    category: "Packages / kits",
    is_active: true,
    assigned_monthly_charge: "",
    lines: [{ product: "", quantity: 1 }],
  });
  const [packageConfirmOpen, setPackageConfirmOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [touchscreenRequiredOpen, setTouchscreenRequiredOpen] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Reset inline add state when tab changes
  useEffect(() => {
    if (inlineAdding) {
      setInlineAdding(false);
      const resetProductType = filterType === "all" ? "product" : filterType;
      setInlineForm({
        name: "",
        category: "",
        price: "",
        product_type: resetProductType,
      });
    }
  }, [filterType]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let nextPath = "/settings/products-services/";
      const collected = [];
      const seen = new Set();

      while (nextPath && !seen.has(nextPath)) {
        seen.add(nextPath);
        const res = await API.get(nextPath);
        const payload = res?.data;

        if (Array.isArray(payload)) {
          collected.push(...payload);
          nextPath = paginationNextPath(res);
          continue;
        }

        const pageRows = Array.isArray(payload?.results) ? payload.results : [];
        collected.push(...pageRows);

        const nextUrl = payload?.next;
        if (!nextUrl) {
          nextPath = null;
        } else {
          // API client already has baseURL, so keep only relative API path.
          nextPath = nextUrl.includes("/api/")
            ? nextUrl.split("/api/")[1]
            : nextUrl;
        }
      }

      setProducts(collected);
    } catch (err) {
      console.error("Failed to load products & services", err);
      toast.error("Failed to load products & services");
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      let nextPath = "/settings/product-packages/?include_inactive=1";
      const collected = [];
      const seen = new Set();
      while (nextPath && !seen.has(nextPath)) {
        seen.add(nextPath);
        const res = await API.get(nextPath);
        const payload = res?.data;
        if (Array.isArray(payload)) {
          collected.push(...payload);
          nextPath = paginationNextPath(res);
          continue;
        }
        const pageRows = Array.isArray(payload?.results) ? payload.results : [];
        collected.push(...pageRows);
        const nextUrl = payload?.next;
        if (!nextUrl) {
          nextPath = null;
        } else {
          nextPath = nextUrl.includes("/api/")
            ? nextUrl.split("/api/")[1]
            : nextUrl;
        }
      }
      setPackages(collected);
    } catch (err) {
      console.error("Failed to load packages", err);
      toast.error("Failed to load packages");
    } finally {
      setPackagesLoading(false);
    }
  };

  useEffect(() => {
    if (filterType === "packages") {
      fetchPackages();
    }
  }, [filterType]);

  const handleOpenDialog = (product = null) => {
    if (isReadOnly) return; // Kill the click for read-only users
    
    setCategoryError(""); // Clear error when opening dialog
    
    if (product) {
      const adtOptions = Array.isArray(product.adt_price_options) ? product.adt_price_options : [];
      const currentPrice = Number(product.price);
      const currentPriceValue = Number.isFinite(currentPrice) ? currentPrice.toFixed(2) : "";
      setEditingProduct(product);
      setProductForm({
        name: product.name || "",
        description: product.description || "",
        price: adtOptions.length
          ? (adtOptions.includes(currentPriceValue) ? currentPriceValue : adtOptions[0])
          : (product.price || ""),
        sku: product.sku || "",
        product_type: product.product_type || "product",
        category: product.category || "",
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: "",
        description: "",
        price: "",
        sku: "",
        product_type: "product",
        category: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setCategoryError(""); // Clear error when closing
    setProductForm({
      name: "",
      description: "",
      price: "",
      sku: "",
      product_type: "product",
      category: "",
    });
  };

  const handleSaveProduct = async () => {
    if (isReadOnly) return; // Kill the click for read-only users
    
    // Validate category
    if (!productForm.category || productForm.category.trim() === "") {
      setCategoryError("Category is required");
      return;
    }
    
    setCategoryError(""); // Clear error if validation passes
    
    try {
      setSaving(true);

      const payload = {
        ...productForm,
        name: capitalizeWords(productForm.name),
        description: capitalizeWords(productForm.description),
        sku: capitalizeWords(productForm.sku),
      };

      if (editingProduct) {
        await API.put(
          `/settings/products-services/${editingProduct.id}/`,
          payload
        );
        toast.success("Item updated successfully");
      } else {
        await API.post("/settings/products-services/", payload);
        toast.success("Item added successfully");
      }
      handleCloseDialog();
      fetchProducts();
    } catch (err) {
      console.error("Failed to save item", err);
      toast.error("Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const handleInlineSave = async () => {
    if (isReadOnly) return;
    
    // Validate required fields
    if (!inlineForm.name || inlineForm.name.trim() === "") {
      return;
    }
    if (!inlineForm.category || inlineForm.category.trim() === "") {
      return;
    }
    
    try {
      setInlineSaving(true);
      
      const payload = {
        name: capitalizeWords(inlineForm.name),
        category: inlineForm.category,
        price: inlineForm.price || "",
        product_type: inlineForm.product_type,
        description: "",
        sku: "",
      };
      
      await API.post("/settings/products-services/", payload);
      toast.success("Item added successfully");
      
      // Reset inline form - preserve filterType
      const resetProductType = filterType === "all" ? "product" : filterType;
      setInlineForm({
        name: "",
        category: "",
        price: "",
        product_type: resetProductType,
      });
      setInlineAdding(false);
      fetchProducts();
    } catch (err) {
      console.error("Failed to save item", err);
      toast.error("Failed to save item");
    } finally {
      setInlineSaving(false);
    }
  };

  const handleDeleteClick = (product) => {
    if (isReadOnly) return; // Kill the click for read-only users
    
    setProductToDelete(product);
    setConfirmDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    try {
      await API.delete(`/settings/products-services/${productToDelete.id}/`);
      toast.success("Item deleted successfully");
      fetchProducts();
    } catch (err) {
      console.error("Failed to delete item", err);
      toast.error("Failed to delete item");
    } finally {
      setProductToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDialogOpen(false);
    setProductToDelete(null);
  };

  const catalogProductsForPackageLines = products.filter(
    (p) => p.product_type === "product" || p.product_type === "service"
  );

  const handleOpenPackageDialog = (pkg = null) => {
    if (isReadOnly) return;
    if (pkg) {
      setEditingPackage(pkg);
      setPackageForm({
        name: pkg.name || "",
        description: pkg.description || "",
        sku: pkg.sku || "",
        category: pkg.category || "Packages / kits",
        is_active: pkg.is_active !== false,
        assigned_monthly_charge: pkg.assigned_monthly_charge ? String(pkg.assigned_monthly_charge) : "",
        lines:
          (pkg.lines || []).length > 0
            ? pkg.lines.map((line) => ({
                product: String(line.product),
                quantity: line.quantity || 1,
              }))
            : [{ product: "", quantity: 1 }],
      });
    } else {
      setEditingPackage(null);
      setPackageForm({
        name: "",
        description: "",
        sku: "",
        category: "Packages / kits",
        is_active: true,
        assigned_monthly_charge: "",
        lines: [{ product: "", quantity: 1 }],
      });
    }
    setPackageDialogOpen(true);
  };

  const handleClosePackageDialog = () => {
    setPackageDialogOpen(false);
    setEditingPackage(null);
    setPackageForm({
      name: "",
      description: "",
      sku: "",
      category: "Packages / kits",
      is_active: true,
      assigned_monthly_charge: "",
      lines: [{ product: "", quantity: 1 }],
    });
  };

  const handleSavePackage = async () => {
    if (isReadOnly) return;
    if (!packageForm.name?.trim()) {
      toast.error("Package name is required");
      return;
    }
    const linesPayload = [];
    for (let i = 0; i < packageForm.lines.length; i++) {
      const row = packageForm.lines[i];
      if (!row.product) continue;
      const qty = parseInt(row.quantity, 10) || 1;
      linesPayload.push({
        product: parseInt(row.product, 10),
        quantity: qty,
        sort_order: i,
      });
    }
    if (linesPayload.length === 0) {
      toast.error("Add at least one catalog product or service to the package");
      return;
    }
    const assignedMonthly = products.find(
      (product) => String(product.id) === String(packageForm.assigned_monthly_charge)
    );
    if (assignedMonthly && WITH_TOUCHSCREEN_PACKAGE_KEYS.has(assignedMonthly.adt_package_key)) {
      const hasTouchscreen = linesPayload.some((line) => {
        if ((line.quantity || 0) < 1) return false;
        const product = products.find((item) => String(item.id) === String(line.product));
        return catalogItemIsTouchscreen(product);
      });
      if (!hasTouchscreen) {
        setTouchscreenRequiredOpen(true);
        return;
      }
    }
    try {
      setPackageSaving(true);
      const body = {
        name: capitalizeWords(packageForm.name),
        description: capitalizeWords(packageForm.description),
        sku: capitalizeWords(packageForm.sku),
        category: packageForm.category?.trim() || "Packages / kits",
        is_active: packageForm.is_active,
        assigned_monthly_charge: packageForm.assigned_monthly_charge
          ? parseInt(packageForm.assigned_monthly_charge, 10)
          : null,
        lines: linesPayload,
      };
      if (editingPackage) {
        await API.put(`/settings/product-packages/${editingPackage.id}/`, body);
        toast.success("Package updated");
      } else {
        await API.post("/settings/product-packages/", body);
        toast.success("Package created");
      }
      handleClosePackageDialog();
      fetchPackages();
    } catch (err) {
      console.error("Failed to save package", err);
      const msg =
        err.response?.data?.detail ||
        (typeof err.response?.data === "string"
          ? err.response.data
          : JSON.stringify(err.response?.data || {})) ||
        "Failed to save package";
      toast.error(msg);
    } finally {
      setPackageSaving(false);
    }
  };

  const handleDeletePackageClick = (pkg) => {
    if (isReadOnly) return;
    setPackageToDelete(pkg);
    setPackageConfirmOpen(true);
  };

  const handleDeletePackageConfirm = async () => {
    if (!packageToDelete) return;
    try {
      await API.delete(`/settings/product-packages/${packageToDelete.id}/`);
      toast.success("Package deleted");
      fetchPackages();
    } catch (err) {
      console.error("Failed to delete package", err);
      toast.error("Failed to delete package");
    } finally {
      setPackageToDelete(null);
      setPackageConfirmOpen(false);
    }
  };

  const addPackageLineRow = () => {
    setPackageForm((prev) => ({
      ...prev,
      lines: [...prev.lines, { product: "", quantity: 1 }],
    }));
  };

  const removePackageLineRow = (index) => {
    setPackageForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index),
    }));
  };

  // Filter products based on selected tab
  const filteredProducts = products.filter((product) => {
    if (filterType === "packages") return false;
    if (filterType === "all") return true;
    return product.product_type === filterType;
  });
  if (filterType === "monthly_charge") {
    filteredProducts.sort(compareMonthlyCharges);
  }

  const mobileProductCardSx = {
    p: 2.25,
    borderRadius: 3,
    bgcolor: (t) =>
      t.palette.mode === "dark"
        ? "rgba(30,41,59,0.88)"
        : "background.paper",
    border: "1px solid",
    borderColor: (t) =>
      t.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.06)",
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? "0 8px 24px rgba(0,0,0,0.55)"
        : "0 4px 12px rgba(0,0,0,0.12)",
    "&:active": { transform: "scale(0.995)" },
  };

  const handleMenuOpen = (e, product) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuProduct(null);
  };

  const getTypeLabel = (product) =>
    product.product_type_display || product.product_type || "—";

  const getTypeColor = (type) =>
    type === "product"
      ? "primary"
      : type === "service"
        ? "secondary"
        : type === "monthly_charge"
          ? "success"
          : "warning";

  // Common category suggestions
  const getCategorySuggestions = (type) => {
    if (type === "product") {
      return ["Video Device", "Automation", "Security Systems", "Pest Control", "Solar", "HVAC", "Plumbing", "Electrical"];
    } else if (type === "service") {
      return ["Installation", "Maintenance", "Repair", "Consultation", "Inspection", "Treatment"];
    } else if (type === "monthly_charge") {
      return ["Monitoring", "Maintenance Plan", "Service Contract", "Subscription", "Support Plan"];
    } else if (type === "discount") {
      return ["Early Bird", "Bulk Discount", "Loyalty", "Seasonal", "Promotional", "Referral"];
    }
    return [];
  };

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header Card */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <InventoryIcon color="primary" />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Products & Services
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your company's products and services catalog
                </Typography>
              </Box>
            </Stack>

            <Button
              data-tour="products-services-add"
              variant="contained"
              startIcon={filterType === "packages" ? <ViewListIcon /> : <AddIcon />}
              onClick={() =>
                filterType === "packages"
                  ? handleOpenPackageDialog()
                  : handleOpenDialog()
              }
              sx={{
                borderRadius: "14px",
                fontWeight: 700,
                fontSize: "0.875rem",
                letterSpacing: "-0.01em",
                lineHeight: 1.4,
                padding: "8px 16px",
                whiteSpace: "nowrap",
                width: { xs: "100%", md: "auto" },
                mt: { xs: 2, md: 0 },
                transition: "all 0.18s ease",
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: "#fff",
                boxShadow: (theme) => `0 10px 22px ${theme.palette.primary.main}47`,
                "&:hover": {
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: (theme) => `0 14px 28px ${theme.palette.primary.main}61`,
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                  boxShadow: (theme) => `0 6px 14px ${theme.palette.primary.main}40`,
                },
              }}
            >
              {filterType === "packages" ? "Add Package" : "Add Product/Service"}
            </Button>
          </Stack>
        </Card>

        {/* Filter Tabs / Pills */}
        <Card sx={{ borderRadius: 3, mb: 2, p: 2 }} data-tour="products-services-tabs">
          {isMobile ? (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
                Type
              </Typography>
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue != null) setFilterType(newValue);
                }}
                size="small"
                sx={{
                  flexWrap: "wrap",
                  gap: 0.5,
                  "& .MuiToggleButtonGroup-grouped": {
                    borderRadius: "20px !important",
                    border: "1px solid",
                    borderColor: "divider",
                    px: 1.25,
                    py: 0.5,
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.813rem",
                  },
                  "& .Mui-selected": {
                    bgcolor: "action.selected",
                    borderColor: "primary.main",
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="product">Products</ToggleButton>
                <ToggleButton value="service">Services</ToggleButton>
                <ToggleButton value="monthly_charge">Monthly</ToggleButton>
                <ToggleButton value="discount">Discounts</ToggleButton>
                <ToggleButton value="packages">Packages</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : (
            <Tabs
              value={filterType}
              onChange={(e, newValue) => setFilterType(newValue)}
              sx={{ px: 2, minHeight: 0 }}
            >
              <Tab label="All" value="all" />
              <Tab label="Products" value="product" />
              <Tab label="Services" value="service" />
              <Tab label="Monthly Charges" value="monthly_charge" />
              <Tab label="Discounts" value="discount" />
              <Tab label="Packages / Kits" value="packages" />
            </Tabs>
          )}
        </Card>

        {/* -------------------------------------------------------Products Table / Mobile Cards */}
        <Card sx={{ borderRadius: 3 }} data-tour="products-services-table">
          <CardContent sx={{ p: isMobile ? { xs: 1, sm: 2 } : undefined }}>
            {filterType === "packages" ? (
              packagesLoading ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  Loading packages…
                </Typography>
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {packages.length === 0 ? (
                    <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                      No packages yet. Use &quot;Add Package&quot; to build a kit from your catalog.
                    </Typography>
                  ) : (
                    packages.map((pkg) => (
                      <Card key={pkg.id} sx={{ p: 2, borderRadius: 2, ...mobileProductCardSx }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Typography fontWeight={700}>{pkg.name}</Typography>
                            <Chip
                              size="small"
                              label={pkg.is_active === false ? "Inactive" : "Active"}
                              color={pkg.is_active === false ? "default" : "success"}
                              variant="outlined"
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {pkg.category || "Packages / kits"} · {(pkg.lines || []).length} line
                            {(pkg.lines || []).length === 1 ? "" : "s"}
                          </Typography>
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isReadOnly}
                              onClick={() => handleOpenPackageDialog(pkg)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isReadOnly}
                              onClick={() => handleDeletePackageClick(pkg)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Card>
                    ))
                  )}
                </Stack>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Package name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Lines</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {packages.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No packages yet. Use &quot;Add Package&quot; to build a kit from your catalog.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        packages.map((pkg) => (
                          <TableRow key={pkg.id}>
                            <TableCell sx={{ fontWeight: 600 }}>{pkg.name}</TableCell>
                            <TableCell>{pkg.category || "Packages / kits"}</TableCell>
                            <TableCell align="right">{(pkg.lines || []).length}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={pkg.is_active === false ? "Inactive" : "Active"}
                                color={pkg.is_active === false ? "default" : "success"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Pencil size={15} strokeWidth={2.15} />}
                                color="primary"
                                disabled={isReadOnly}
                                onClick={() => handleOpenPackageDialog(pkg)}
                                sx={{
                                  minWidth: 82,
                                  height: 34,
                                  px: 1.5,
                                  borderRadius: 2,
                                  textTransform: "none",
                                  fontWeight: 600,
                                }}
                              >
                                Edit
                              </Button>
                              <IconButton size="small" color="error" disabled={isReadOnly} onClick={() => handleDeletePackageClick(pkg)}>
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            ) : isMobile ? (
              /* --------------- Mobile Card View --------------- */
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Inline Add Card (mobile) */}
                {inlineAdding && (
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Stack spacing={2}>
                      {filterType === "all" && (
                        <FormControl size="small" fullWidth>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={inlineForm.product_type}
                            onChange={(e) =>
                              setInlineForm({
                                ...inlineForm,
                                product_type: e.target.value,
                                category: "",
                              })
                            }
                            label="Type"
                            disabled={isReadOnly || inlineSaving}
                          >
                            <MenuItem value="product">Product</MenuItem>
                            <MenuItem value="service">Service</MenuItem>
                            <MenuItem value="monthly_charge">Monthly Charge</MenuItem>
                            <MenuItem value="discount">Discount</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                      <TextField
                        size="small"
                        placeholder="Name"
                        value={inlineForm.name}
                        onChange={(e) =>
                          setInlineForm({ ...inlineForm, name: e.target.value })
                        }
                        disabled={isReadOnly || inlineSaving}
                        fullWidth
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={inlineForm.category}
                          onChange={(e) =>
                            setInlineForm({ ...inlineForm, category: e.target.value })
                          }
                          label="Category"
                          disabled={isReadOnly || inlineSaving}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select...</em>
                          </MenuItem>
                          {getCategorySuggestions(inlineForm.product_type).map(
                            (cat) => (
                              <MenuItem key={cat} value={cat}>
                                {cat}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Price (0.00)"
                        value={inlineForm.price}
                        onChange={(e) =>
                          setInlineForm({ ...inlineForm, price: e.target.value })
                        }
                        disabled={isReadOnly || inlineSaving}
                        inputProps={{ step: "0.01", min: "0" }}
                        fullWidth
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleInlineSave}
                          disabled={
                            isReadOnly ||
                            inlineSaving ||
                            !inlineForm.name.trim() ||
                            !inlineForm.category
                          }
                          sx={{ flex: 1 }}
                        >
                          {inlineSaving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setInlineAdding(false);
                            const resetProductType =
                              filterType === "all" ? "product" : filterType;
                            setInlineForm({
                              name: "",
                              category: "",
                              price: "",
                              product_type: resetProductType,
                            });
                          }}
                          disabled={inlineSaving}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  </Card>
                )}

                {filteredProducts.length === 0 && !inlineAdding ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary" gutterBottom>
                      {products.length === 0
                        ? 'No products or services yet. Click "Add Product/Service" to get started.'
                        : `No ${filterType === "product" ? "products" : filterType === "service" ? "services" : filterType === "monthly_charge" ? "monthly charges" : filterType === "discount" ? "discounts" : "items"} found.`}
                    </Typography>
                  </Box>
                ) : (
                  filteredProducts.map((product) => (
                    <Card key={product.id} sx={mobileProductCardSx}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {/* Identity row */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.06)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <InventoryIcon
                              sx={{
                                fontSize: 22,
                                color: "text.secondary",
                              }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{ lineHeight: 1.2 }}
                            >
                              {product.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: 13,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {product.description || "—"}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, product)}
                            sx={{
                              position: "absolute",
                              top: -4,
                              right: -4,
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Stats grid */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600, display: "block", mb: 0.25 }}
                            >
                              Type
                            </Typography>
                            <Chip
                              label={getTypeLabel(product)}
                              size="small"
                              color={getTypeColor(product.product_type)}
                              variant="outlined"
                              sx={{
                                textTransform: "capitalize",
                                borderRadius: "6px",
                                fontSize: "0.75rem",
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600, display: "block", mb: 0.25 }}
                            >
                              Category
                            </Typography>
                            <Chip
                              label={product.category || "—"}
                              size="small"
                              variant="outlined"
                              sx={{
                                textTransform: "capitalize",
                                borderRadius: "6px",
                                fontSize: "0.75rem",
                                maxWidth: "100%",
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600, display: "block", mb: 0.25 }}
                            >
                              Price
                            </Typography>
                            <Typography variant="body2">
                              {product.price
                                ? `$${parseFloat(product.price).toFixed(2)}`
                                : "—"}
                            </Typography>
                          </Box>
                          {filterType !== "monthly_charge" &&
                            filterType !== "discount" && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 600,
                                    display: "block",
                                    mb: 0.25,
                                  }}
                                >
                                  SKU
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ textTransform: "uppercase" }}
                                >
                                  {product.sku || "—"}
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      </Box>
                    </Card>
                  ))
                )}

              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    {filterType !== "monthly_charge" && filterType !== "discount" && (
                      <TableCell><strong>SKU</strong></TableCell>
                    )}
                    <TableCell><strong>Price</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Inline Add Row */}
                  {inlineAdding && (
                    <TableRow sx={{ bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)" }}>
                      <TableCell>
                        {filterType === "all" ? (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={inlineForm.product_type}
                              onChange={(e) => setInlineForm({...inlineForm, product_type: e.target.value, category: ""})}
                              disabled={isReadOnly || inlineSaving}
                            >
                              <MenuItem value="product">Product</MenuItem>
                              <MenuItem value="service">Service</MenuItem>
                              <MenuItem value="monthly_charge">Monthly Charge</MenuItem>
                              <MenuItem value="discount">Discount</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={inlineForm.product_type === "product" ? "Product"
                              : inlineForm.product_type === "service" ? "Service"
                              : inlineForm.product_type === "monthly_charge" ? "Monthly Charge"
                              : "Discount"}
                            size="small"
                            color={
                              inlineForm.product_type === "product" ? "primary" 
                              : inlineForm.product_type === "service" ? "secondary"
                              : inlineForm.product_type === "monthly_charge" ? "success"
                              : "warning"
                            }
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Name"
                          value={inlineForm.name}
                          onChange={(e) => setInlineForm({...inlineForm, name: e.target.value})}
                          disabled={isReadOnly || inlineSaving}
                          autoFocus
                          sx={{ minWidth: 150 }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 150 }} required error={!inlineForm.category && inlineForm.name.trim() !== ""}>
                          <Select
                            value={inlineForm.category}
                            onChange={(e) => setInlineForm({...inlineForm, category: e.target.value})}
                            disabled={isReadOnly || inlineSaving}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>Select...</em>
                            </MenuItem>
                            {getCategorySuggestions(inlineForm.product_type).map((cat) => (
                              <MenuItem key={cat} value={cat}>
                                {cat}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      {filterType !== "monthly_charge" && filterType !== "discount" && (
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            (Optional)
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="0.00"
                          value={inlineForm.price}
                          onChange={(e) => setInlineForm({...inlineForm, price: e.target.value})}
                          disabled={isReadOnly || inlineSaving}
                          inputProps={{ step: "0.01", min: "0" }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                          (Optional)
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleInlineSave}
                            disabled={isReadOnly || inlineSaving || !inlineForm.name.trim() || !inlineForm.category}
                            sx={{ minWidth: "auto", px: 1.5 }}
                          >
                            {inlineSaving ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setInlineAdding(false);
                              const resetProductType = filterType === "all" ? "product" : filterType;
                              setInlineForm({ name: "", category: "", price: "", product_type: resetProductType });
                            }}
                            disabled={inlineSaving}
                            sx={{ minWidth: "auto", px: 1.5 }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {filteredProducts.length === 0 && !inlineAdding ? (
                    <TableRow>
                      <TableCell colSpan={filterType === "monthly_charge" || filterType === "discount" ? 6 : 7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {products.length === 0
                            ? 'No products or services yet. Click "Add Product/Service" to get started.'
                            : `No ${filterType === "product" ? "products" 
                                : filterType === "service" ? "services"
                                : filterType === "monthly_charge" ? "monthly charges"
                                : filterType === "discount" ? "discounts"
                                : "items"} found.`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Chip
                            label={product.product_type_display || product.product_type}
                            size="small"
                            color={
                              product.product_type === "product" ? "primary" 
                              : product.product_type === "service" ? "secondary"
                              : product.product_type === "monthly_charge" ? "success"
                              : "warning"
                            }
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{product.name}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{product.category || "-"}</TableCell>
                        {filterType !== "monthly_charge" && filterType !== "discount" && (
                          <TableCell sx={{ textTransform: 'uppercase' }}>{product.sku || "-"}</TableCell>
                        )}
                        <TableCell>
                          {product.price ? `$${parseFloat(product.price).toFixed(2)}` : "-"}
                        </TableCell>
                        <TableCell>
                          {product.description ? (
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textTransform: 'capitalize',
                              }}
                            >
                              {product.description}
                            </Typography>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <DisabledButtonWrapper 
                            isDisabled={isReadOnly}
                            tooltipText="Read-only mode: This action is disabled"
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Pencil size={15} strokeWidth={2.15} />}
                              onClick={() => handleOpenDialog(product)}
                              color="primary"
                              disabled={isReadOnly}
                              sx={{
                                minWidth: 82,
                                height: 34,
                                px: 1.5,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                opacity: isReadOnly ? 0.6 : 1,
                                cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                visibility: 'visible',
                                display: 'inline-flex',
                              }}
                            >
                              Edit
                            </Button>
                          </DisabledButtonWrapper>
                          <DisabledButtonWrapper 
                            isDisabled={isReadOnly}
                            tooltipText="Read-only mode: This action is disabled"
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(product)}
                              color="error"
                              disabled={isReadOnly}
                              sx={{
                                opacity: isReadOnly ? 0.6 : 1,
                                cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                visibility: 'visible',
                                display: 'inline-flex',
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </DisabledButtonWrapper>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  
                  {/* Add Item Button Row (when not in inline mode) */}
                  {!inlineAdding && !isReadOnly && (
                    <TableRow>
                      <TableCell colSpan={filterType === "monthly_charge" || filterType === "discount" ? 6 : 7} align="center" sx={{ py: 1.5, borderTop: "1px dashed", borderColor: "divider" }}>
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const productType = filterType === "all" ? "product" : filterType;
                            setInlineForm({
                              name: "",
                              category: "",
                              price: "",
                              product_type: productType,
                            });
                            setInlineAdding(true);
                          }}
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: "none" }}
                        >
                          Quick Add Item
                        </Button>
                        <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
                          or use "Add Product/Service" button for full form
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* --------------------------------------------------Package Dialog */}
        <Dialog
          open={packageDialogOpen}
          onClose={handleClosePackageDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "background.paper",
            },
          }}
        >
          <DialogTitle
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2.25,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(59,130,246,0.14)"
                      : "rgba(59,130,246,0.09)",
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                <Package size={20} strokeWidth={2} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {editingPackage ? "Edit Package / Kit" : "Add Package / Kit"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {editingPackage
                    ? "Update the package details and included catalog items."
                    : "Build a reusable package from products and services in your catalog."}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 2.25 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2.5,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.025)"
                      : "rgba(248,250,252,0.72)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1.75,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(59,130,246,0.14)"
                          : "rgba(59,130,246,0.09)",
                      color: "primary.main",
                    }}
                  >
                    <Package size={16} strokeWidth={2} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Basic Information
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <TextField
                    label="Package name"
                    value={packageForm.name}
                    onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                    fullWidth
                    required
                    disabled={isReadOnly}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        transition: "box-shadow 160ms ease",
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "text.secondary" },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${
                              theme.palette.mode === "dark"
                                ? "rgba(59,130,246,0.16)"
                                : "rgba(37,99,235,0.10)"
                            }`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                    }}
                  />

                  <TextField
                    label="SKU (optional)"
                    value={packageForm.sku}
                    onChange={(e) => setPackageForm({ ...packageForm, sku: e.target.value })}
                    fullWidth
                    disabled={isReadOnly}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        transition: "box-shadow 160ms ease",
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "text.secondary" },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${
                              theme.palette.mode === "dark"
                                ? "rgba(59,130,246,0.16)"
                                : "rgba(37,99,235,0.10)"
                            }`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                    }}
                  />

                  <TextField
                    label="Description"
                    value={packageForm.description}
                    onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                    fullWidth
                    multiline
                    rows={3}
                    disabled={isReadOnly}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        transition: "box-shadow 160ms ease",
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "text.secondary" },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${
                              theme.palette.mode === "dark"
                                ? "rgba(59,130,246,0.16)"
                                : "rgba(37,99,235,0.10)"
                            }`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                    }}
                  />
                </Stack>
              </Box>

              <Stack spacing={2}>
                <Box
                  sx={{
                    p: { xs: 2, sm: 2.25 },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.025)"
                        : "rgba(248,250,252,0.72)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(99,102,241,0.14)"
                            : "rgba(99,102,241,0.09)",
                        color: "primary.main",
                      }}
                    >
                      <Tags size={16} strokeWidth={2} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Classification
                    </Typography>
                  </Box>

                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>
                      Category
                    </InputLabel>
                    <Select
                      label="Category"
                      value={packageForm.category}
                      onChange={(e) => setPackageForm({ ...packageForm, category: e.target.value })}
                      disabled={isReadOnly}
                      sx={{
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "text.secondary" },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${
                              theme.palette.mode === "dark"
                                ? "rgba(59,130,246,0.16)"
                                : "rgba(37,99,235,0.10)"
                            }`,
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 0.75,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: (theme) =>
                              theme.palette.mode === "dark"
                                ? "0 14px 34px rgba(0,0,0,0.45)"
                                : "0 14px 34px rgba(15,23,42,0.14)",
                            "& .MuiMenuItem-root": {
                              minHeight: 42,
                              mx: 0.75,
                              my: 0.25,
                              borderRadius: 1.75,
                              fontSize: "0.875rem",
                            },
                            "& .MuiMenuItem-root.Mui-selected": {
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(59,130,246,0.16)"
                                  : "rgba(37,99,235,0.08)",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="Packages / kits">Packages / kits</MenuItem>
                      <MenuItem value="Residential kits">Residential kits</MenuItem>
                      <MenuItem value="Commercial kits">Commercial kits</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    p: { xs: 2, sm: 2.25 },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.025)"
                        : "rgba(248,250,252,0.72)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(34,197,94,0.14)"
                            : "rgba(34,197,94,0.09)",
                        color: "#16a34a",
                      }}
                    >
                      <DollarSign size={16} strokeWidth={2} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Assign monthly charge
                    </Typography>
                  </Box>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>
                      Monthly charge
                    </InputLabel>
                    <Select
                      label="Monthly charge"
                      value={packageForm.assigned_monthly_charge}
                      onChange={(e) =>
                        setPackageForm({ ...packageForm, assigned_monthly_charge: e.target.value })
                      }
                      disabled={isReadOnly}
                      sx={{
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "text.secondary" },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${
                              theme.palette.mode === "dark"
                                ? "rgba(59,130,246,0.16)"
                                : "rgba(37,99,235,0.10)"
                            }`,
                        },
                      }}
                      MenuProps={{
                        disableAutoFocusItem: true,
                        PaperProps: {
                          sx: {
                            maxHeight: 216,
                            mt: 0.75,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            overflowY: "auto",
                            boxShadow: (theme) =>
                              theme.palette.mode === "dark"
                                ? "0 14px 34px rgba(0,0,0,0.45)"
                                : "0 14px 34px rgba(15,23,42,0.14)",
                            "& .MuiMenuItem-root": {
                              minHeight: 40,
                              height: 40,
                              mx: 0.75,
                              my: 0,
                              borderRadius: 1.5,
                              fontSize: "0.875rem",
                            },
                            "& .MuiMenuItem-root.Mui-selected": {
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(59,130,246,0.16)"
                                  : "rgba(37,99,235,0.08)",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="">Any monthly charge</MenuItem>
                      {products
                        .filter((product) => product.product_type === "monthly_charge")
                        .slice()
                        .sort(compareMonthlyCharges)
                        .map((product) => (
                          <MenuItem key={product.id} value={String(product.id)}>
                            {product.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    p: { xs: 2, sm: 2.25 },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.025)"
                        : "rgba(248,250,252,0.72)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Package availability
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Inactive packages are hidden from proposals.
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={packageForm.is_active}
                      onChange={(e) =>
                        setPackageForm({ ...packageForm, is_active: e.target.checked })
                      }
                      disabled={isReadOnly}
                    />
                  </Box>
                </Box>
              </Stack>

              <Box
                sx={{
                  gridColumn: { xs: "auto", md: "1 / -1" },
                  p: { xs: 2, sm: 2.25 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2.5,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.025)"
                      : "rgba(248,250,252,0.72)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(59,130,246,0.14)"
                            : "rgba(59,130,246,0.09)",
                        color: "primary.main",
                      }}
                    >
                      <Package size={16} strokeWidth={2} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Catalog Lines
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Choose the products and quantities included in this package.
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={addPackageLineRow}
                    disabled={isReadOnly}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >
                    Add line
                  </Button>
                </Box>

                <Stack spacing={1.25}>
                  {packageForm.lines.map((row, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 90px 38px" },
                        gap: 1,
                        alignItems: "center",
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 0 }} fullWidth>
                        <InputLabel>Product / service</InputLabel>
                        <Select
                          label="Product / service"
                          value={row.product}
                          onChange={(e) => {
                            const next = [...packageForm.lines];
                            next[index] = { ...next[index], product: e.target.value };
                            setPackageForm({ ...packageForm, lines: next });
                          }}
                          disabled={isReadOnly}
                          sx={{ borderRadius: 2 }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 320,
                                mt: 0.75,
                                borderRadius: 2.5,
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "0 14px 34px rgba(0,0,0,0.45)"
                                    : "0 14px 34px rgba(15,23,42,0.14)",
                                "& .MuiMenuItem-root": {
                                  minHeight: 42,
                                  mx: 0.75,
                                  my: 0.25,
                                  borderRadius: 1.75,
                                  fontSize: "0.875rem",
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select…</em>
                          </MenuItem>
                          {catalogProductsForPackageLines.map((p) => (
                            <MenuItem key={p.id} value={String(p.id)}>
                              {p.name} — ${parseFloat(p.price || 0).toFixed(2)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        size="small"
                        type="number"
                        label="Qty"
                        value={row.quantity}
                        onChange={(e) => {
                          const next = [...packageForm.lines];
                          next[index] = {
                            ...next[index],
                            quantity: parseInt(e.target.value, 10) || 1,
                          };
                          setPackageForm({ ...packageForm, lines: next });
                        }}
                        disabled={isReadOnly}
                        inputProps={{ min: 1 }}
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": { borderRadius: 2 },
                        }}
                      />

                      <IconButton
                        color="error"
                        disabled={isReadOnly || packageForm.lines.length <= 1}
                        onClick={() => removePackageLineRow(index)}
                        aria-label="Remove line"
                        sx={{
                          width: 36,
                          height: 36,
                          justifySelf: { xs: "end", sm: "center" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              gap: 1,
            }}
          >
            <Button
              onClick={handleClosePackageDialog}
              variant="outlined"
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSavePackage}
              disabled={isReadOnly || packageSaving || !packageForm.name?.trim()}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              {packageSaving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={touchscreenRequiredOpen}
          onClose={() => setTouchscreenRequiredOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            This configuration requires a touchscreen add on
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: "text.secondary", lineHeight: 1.5 }}>
              Add a touchscreen to the kit or select a different monthly service package.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="contained"
              onClick={() => setTouchscreenRequiredOpen(false)}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* --------------------------------------------------Add/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "background.paper",
            },
          }}
        >
          <DialogTitle
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2.25,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(59,130,246,0.14)"
                      : "rgba(59,130,246,0.09)",
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                <Package size={20} strokeWidth={2} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {editingProduct ? "Edit Product or Service" : "Add Product or Service"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {editingProduct ? "Update the product details below." : "Add a new product or service to your catalog."}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 2.25 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2.5,
                  bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.025)" : "rgba(248,250,252,0.72)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: 1.75, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(59,130,246,0.14)" : "rgba(59,130,246,0.09)", color: "primary.main" }}>
                    <Package size={16} strokeWidth={2} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Basic Information</Typography>
                </Box>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    value={productForm.name}
                    onChange={(e) =>
                      setProductForm({ ...productForm, name: e.target.value })
                    }
                    fullWidth
                    required
                    placeholder={
                      productForm.product_type === "product"
                        ? "e.g., Security Camera System"
                        : productForm.product_type === "service"
                        ? "e.g., Alarm System Installation"
                        : productForm.product_type === "monthly_charge"
                        ? "e.g., Monthly Monitoring Service"
                        : "e.g., Early Bird Discount"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        transition: "box-shadow 160ms ease",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "text.secondary",
                        },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${theme.palette.mode === "dark"
                              ? "rgba(59,130,246,0.16)"
                              : "rgba(37,99,235,0.10)"}`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  />
                  <TextField
                    label="SKU"
                    value={productForm.sku}
                    onChange={(e) =>
                      setProductForm({ ...productForm, sku: e.target.value })
                    }
                    fullWidth
                    placeholder="e.g., SEC-001"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        transition: "box-shadow 160ms ease",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "text.secondary",
                        },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${theme.palette.mode === "dark"
                              ? "rgba(59,130,246,0.16)"
                              : "rgba(37,99,235,0.10)"}`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  />
                  <TextField
                    label="Description"
                    value={productForm.description}
                    onChange={(e) =>
                      setProductForm({ ...productForm, description: e.target.value })
                    }
                    fullWidth
                    multiline
                    rows={3}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        transition: "box-shadow 160ms ease",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "text.secondary",
                        },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${theme.palette.mode === "dark"
                              ? "rgba(59,130,246,0.16)"
                              : "rgba(37,99,235,0.10)"}`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  />
                </Stack>
              </Box>

              <Stack spacing={2}>
                <Box
                  sx={{
                    p: { xs: 2, sm: 2.25 },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2.5,
                    bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.025)" : "rgba(248,250,252,0.72)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: 1.75, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(249,115,22,0.14)" : "rgba(249,115,22,0.09)", color: "#f97316" }}>
                      <Tags size={16} strokeWidth={2} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Classification</Typography>
                  </Box>
                  <Stack spacing={2}>
<FormControl
                    fullWidth
                    required
                    error={!!categoryError}
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "primary.main" },
                    }}
                  >
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={productForm.category}
                      label="Category"
                    MenuProps={{
                          PaperProps: {
                            sx: {
                              mt: 0.75,
                              borderRadius: 2.5,
                              border: "1px solid",
                              borderColor: "divider",
                              boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "0 14px 36px rgba(0,0,0,0.42)"
                                  : "0 14px 36px rgba(15,23,42,0.14)",
                              overflow: "hidden",
                              py: 0.5,
                              "& .MuiMenuItem-root": {
                                minHeight: 42,
                                mx: 0.75,
                                my: 0.2,
                                px: 1.5,
                                borderRadius: 1.75,
                                fontSize: "0.9rem",
                              },
                              "& .MuiMenuItem-root.Mui-selected": {
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(59,130,246,0.16)"
                                    : "rgba(37,99,235,0.09)",
                              },
                              "& .MuiMenuItem-root.Mui-selected:hover": {
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(59,130,246,0.22)"
                                    : "rgba(37,99,235,0.13)",
                              },
                            },
                          },
                        }}
                    sx={{
                          borderRadius: 2.25,
                          bgcolor: "background.paper",
                          "& .MuiSelect-select": { py: 1.45 },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "text.secondary",
                          },
                          "&.Mui-focused": {
                            boxShadow: (theme) =>
                              `0 0 0 3px ${theme.palette.mode === "dark"
                                ? "rgba(59,130,246,0.16)"
                                : "rgba(37,99,235,0.10)"}`,
                          },
                        }}
                      onChange={(e) => {
                        setProductForm({ ...productForm, category: e.target.value });
                        setCategoryError(""); // Clear error when category changes
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {getCategorySuggestions(productForm.product_type).map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                    {categoryError && (
                      <FormHelperText sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                        {categoryError}
                      </FormHelperText>
                    )}
                  </FormControl>
<FormControl
                    fullWidth
                    required
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "primary.main" },
                    }}
                  >
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={productForm.product_type}
                      label="Type"
                    MenuProps={{
                          PaperProps: {
                            sx: {
                              mt: 0.75,
                              borderRadius: 2.5,
                              border: "1px solid",
                              borderColor: "divider",
                              boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "0 14px 36px rgba(0,0,0,0.42)"
                                  : "0 14px 36px rgba(15,23,42,0.14)",
                              overflow: "hidden",
                              py: 0.5,
                              "& .MuiMenuItem-root": {
                                minHeight: 42,
                                mx: 0.75,
                                my: 0.2,
                                px: 1.5,
                                borderRadius: 1.75,
                                fontSize: "0.9rem",
                              },
                              "& .MuiMenuItem-root.Mui-selected": {
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(59,130,246,0.16)"
                                    : "rgba(37,99,235,0.09)",
                              },
                              "& .MuiMenuItem-root.Mui-selected:hover": {
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(59,130,246,0.22)"
                                    : "rgba(37,99,235,0.13)",
                              },
                            },
                          },
                        }}
                    sx={{
                          borderRadius: 2.25,
                          bgcolor: "background.paper",
                          "& .MuiSelect-select": { py: 1.45 },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "text.secondary",
                          },
                          "&.Mui-focused": {
                            boxShadow: (theme) =>
                              `0 0 0 3px ${theme.palette.mode === "dark"
                                ? "rgba(59,130,246,0.16)"
                                : "rgba(37,99,235,0.10)"}`,
                          },
                        }}
                      onChange={(e) =>
                        setProductForm({ ...productForm, product_type: e.target.value, category: "" })
                      }
                    >
                      <MenuItem value="product">Product</MenuItem>
                      <MenuItem value="service">Service</MenuItem>
                      <MenuItem value="monthly_charge">Monthly Charge</MenuItem>
                      <MenuItem value="discount">Discount</MenuItem>
                    </Select>
                  </FormControl>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: { xs: 2, sm: 2.25 },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2.5,
                    bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.025)" : "rgba(248,250,252,0.72)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: 1.75, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(34,197,94,0.14)" : "rgba(34,197,94,0.09)", color: "#16a34a" }}>
                      <DollarSign size={16} strokeWidth={2} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {editingProduct?.adt_package_key ? "Default Pricing" : "Pricing"}
                    </Typography>
                  </Box>
                {editingProduct?.adt_package_key ? (
                  <FormControl fullWidth>
                    <InputLabel>Default Pricing</InputLabel>
                    <Select
                      value={productForm.price}
                      label="Default Pricing"
                      onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                      MenuProps={{
                        disableAutoFocusItem: true,
                        PaperProps: {
                          sx: {
                            maxHeight: 216,
                            borderRadius: 2.5,
                            mt: 0.75,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: (theme) =>
                              theme.palette.mode === "dark"
                                ? "0 14px 36px rgba(0,0,0,0.42)"
                                : "0 14px 36px rgba(15,23,42,0.14)",
                            overflowY: "auto",
                            "& .MuiList-root": { py: 0.5 },
                            "& .MuiMenuItem-root": {
                              minHeight: 40,
                              height: 40,
                              mx: 0.75,
                              my: 0,
                              px: 1.5,
                              borderRadius: 1.5,
                            },
                          },
                        },
                      }}
                      sx={{
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        "& .MuiSelect-select": { py: 1.45 },
                      }}
                    >
                      {(editingProduct.adt_price_options || []).map((amount) => (
                        <MenuItem key={amount} value={amount}>
                          ${amount}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                <TextField
                  label="Price"
                  type="number"
                  value={productForm.price}
                  onChange={(e) =>
                    setProductForm({ ...productForm, price: e.target.value })
                  }
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: "background.paper",
                        transition: "box-shadow 160ms ease",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "text.secondary",
                        },
                        "&.Mui-focused": {
                          boxShadow: (theme) =>
                            `0 0 0 3px ${theme.palette.mode === "dark"
                              ? "rgba(59,130,246,0.16)"
                              : "rgba(37,99,235,0.10)"}`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                />
                )}
                </Box>
              </Stack>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: 1.75,
              borderTop: "1px solid",
              borderColor: "divider",
              gap: 1,
            }}
          >
            <Button onClick={handleCloseDialog} variant="outlined" sx={{ textTransform: "none", minWidth: 96 }}>
              Cancel
            </Button>
            <DisabledButtonWrapper 
              isDisabled={isReadOnly || saving || !productForm.name || !productForm.category}
              tooltipText={saving ? "Saving in progress..." : !productForm.name ? "Please enter a name" : !productForm.category ? "Please select a category" : "Read-only mode: This action is disabled"}
            >
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveProduct}
                disabled={isReadOnly || saving || !productForm.name || !productForm.category}
                sx={{
                  minWidth: 138,
                  textTransform: "none",
                  opacity: (isReadOnly || saving || !productForm.name || !productForm.category) ? 0.6 : 1,
                  cursor: (isReadOnly || saving || !productForm.name || !productForm.category) ? 'not-allowed' : 'pointer',
                  visibility: 'visible',
                  display: 'inline-flex',
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DisabledButtonWrapper>
          </DialogActions>
        </Dialog>

        {/* Overflow menu (mobile) */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 3,
                minWidth: 100,
                boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                py: 0.25,
                "& .MuiMenuItem-root": {
                  py: 0.5,
                  px: 1.25,
                  fontSize: "0.8125rem",
                },
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              if (menuProduct) handleOpenDialog(menuProduct);
              handleMenuClose();
            }}
            disabled={isReadOnly}
          >
            <EditIcon sx={{ fontSize: 18, mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuProduct) handleDeleteClick(menuProduct);
              handleMenuClose();
            }}
            disabled={isReadOnly}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon sx={{ fontSize: 18, mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Product/Service"
          message="Are you sure you want to delete this item? This action cannot be undone."
          itemName={productToDelete?.name}
          confirmText="Delete"
          cancelText="Cancel"
          severity="error"
        />

        <ConfirmDialog
          open={packageConfirmOpen}
          onClose={() => {
            setPackageConfirmOpen(false);
            setPackageToDelete(null);
          }}
          onConfirm={handleDeletePackageConfirm}
          title="Delete package"
          message="Are you sure you want to delete this package? Proposals that already include its line items will not be changed."
          itemName={packageToDelete?.name}
          confirmText="Delete"
          cancelText="Cancel"
          severity="error"
        />
      </Box>
    </Box>
  );
}
